/**
 * Δομικά στοιχεία του app σερβιτόρου.
 *
 * Αυτό το app τρέχει ΜΟΝΟ σε κινητό. Άρα τα μεγέθη αφής είναι η προεπιλογή,
 * όχι εξαίρεση: κάθε κουμπί εδώ ξεκινάει από 44px (Apple HIG· τα κύρια κουμπιά
 * ενεργειών 48px). Πυκνή διάταξη από 1/10/2026 — ο ιδιοκτήτης ζήτησε «όλα λίγο
 * μικρότερα» στο PDA· τα 44px αφής ΔΕΝ πέφτουν. Δεν υπάρχουν `isMobile`
 * διακλαδώσεις — αν κάτι πρέπει να δείχνει αλλιώς σε ταμπλέτα, το ταμείο έχει
 * τη δική του οθόνη.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, X } from 'lucide-react';
import { formatMoney } from '../../../utils/formatMoney';

/**
 * Τα overlays πάνε σε portal στο <body>. Με `fixed` μέσα στο δέντρο, οποιοσδήποτε
 * γονέας με transform/filter γίνεται containing block και η οθόνη σταματά στη
 * μέση αντί να καλύψει το κινητό.
 */
const Overlay: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  typeof document === 'undefined' ? <>{children}</> : createPortal(<div className="role-workspace role-waiter contents">{children}</div>, document.body);

/** Safe areas + animations σε πραγματικό CSS, ώστε να μην εξαρτόμαστε από
 *  arbitrary Tailwind classes που μπορεί να λείπουν από το build. */
export const WaiterStyles: React.FC = () => (
  <style>{`
    /* Χωρίς «τράβηγμα για ανανέωση» όσο είναι ανοιχτό το app σερβιτόρου: ένα τράβηγμα προς τα κάτω
       στην κορυφή έκανε reload — και χανόταν η παραγγελία που δεν είχε σταλεί (7/10/2026). */
    html, body { overscroll-behavior-y: none; }
    .wa-safe-top { padding-top: max(8px, env(safe-area-inset-top)); }
    .wa-safe-bottom { padding-bottom: max(8px, env(safe-area-inset-bottom)); }
    /* Κάτω κενό της κύριας λίστας = ύψος κάτω πλοήγησης (56 + 1 περίγραμμα +
       wa-safe-bottom) + 7px αέρας, ώστε στο iPhone με home indicator η τελευταία
       σειρά/«Αποσύνδεση» να μη μένει κάτω από τη μπάρα. */
    .wa-main-pad { padding-bottom: calc(64px + max(8px, env(safe-area-inset-bottom))); }
    /* Ελάχιστο ύψος ώστε ένα σύντομο sheet να μη δείχνει σαν λωρίδα στο
       κάτω μέρος — native detent αίσθηση. */
    .wa-sheet { max-height: 92vh; min-height: 48vh; }
    @supports (height: 100dvh) { .wa-sheet { max-height: 92dvh; min-height: 48dvh; } }
    .wa-full { height: 100vh; }
    @supports (height: 100dvh) { .wa-full { height: 100dvh; } }
    @keyframes wa-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
    .wa-animate-up { animation: wa-slide-up .25s ease-out; }
    .wa-scroll { -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
    /* Οριζόντιες λωρίδες (καρτέλες κατηγοριών): κύλιση με το δάχτυλο, χωρίς μπάρα. */
    .wa-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
    .wa-no-scrollbar::-webkit-scrollbar { display: none; }
    /* Μετρητής προϊόντος: μικρό «χτύπημα» σε κάθε προσθήκη — επιβεβαίωση χωρίς να ανοίξει η σύνοψη. */
    @keyframes wa-pop { from { transform: scale(1.4); } to { transform: scale(1); } }
    .wa-pop { animation: wa-pop .18s ease-out; }
    /* Μπάρα «τελευταίο είδος»: μια λάμψη σε κάθε προσθήκη — φαίνεται τι μπήκε χωρίς να κοιτάξεις το πλακίδιο. */
    @keyframes wa-flash { from { background-color: rgb(37 99 235 / .22); } to { background-color: transparent; } }
    .wa-flash { animation: wa-flash .6s ease-out; }
    @media (prefers-reduced-motion: reduce) { .wa-pop, .wa-flash { animation: none; } }
  `}</style>
);

type ButtonTone = 'primary' | 'neutral' | 'success' | 'danger' | 'warning' | 'ghost';

const TONES: Record<ButtonTone, string> = {
  primary: 'bg-blue-600 text-white active:bg-blue-700',
  neutral: 'bg-surface-2 text-t1 active:bg-surface-3',
  success: 'bg-emerald-600 text-white active:bg-emerald-700',
  danger: 'bg-red-600 text-white active:bg-red-700',
  warning: 'bg-orange-600 text-white active:bg-orange-700',
  ghost: 'bg-transparent text-t2 active:bg-surface-2',
};

export const Button: React.FC<{
  onClick?: () => void;
  tone?: ButtonTone;
  disabled?: boolean;
  full?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ onClick, tone = 'neutral', disabled, full, className = '', children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`min-h-[48px] px-4 rounded-xl font-bold text-base flex items-center justify-center gap-2
      transition-transform active:scale-[0.98] light:disabled:opacity-60 dark:disabled:opacity-40 disabled:active:scale-100
      ${TONES[tone]} ${full ? 'w-full' : ''} ${className}`}
  >
    {children}
  </button>
);

/** Εικονίδιο-κουμπί με εγγυημένη περιοχή αφής 44px. */
export const IconButton: React.FC<{
  onClick?: () => void;
  label: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ onClick, label, disabled, className = '', children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`w-[44px] h-[44px] shrink-0 rounded-xl flex items-center justify-center
      text-t2 active:bg-surface-2 disabled:opacity-40 transition-colors ${className}`}
  >
    {children}
  </button>
);

/** Stepper ποσότητας — 44px ανά πλευρά, διαβάζεται με μια ματιά εν ώρα ρουσφετιού. */
export const Stepper: React.FC<{
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
  min?: number;
}> = ({ value, onDecrease, onIncrease, min = 0 }) => (
  <div className="flex items-center bg-surface-2 border border-line-strong dark:border-transparent rounded-xl overflow-hidden">
    <button
      onClick={onDecrease}
      disabled={value <= min}
      aria-label="Μείωση"
      className="w-11 h-11 flex items-center justify-center text-2xl font-bold text-t1 active:bg-surface-3 disabled:opacity-40"
    >
      −
    </button>
    <span className="w-11 text-center text-xl font-bold text-t1 tabular-nums">{value}</span>
    <button
      onClick={onIncrease}
      aria-label="Αύξηση"
      className="w-11 h-11 flex items-center justify-center text-2xl font-bold text-t1 active:bg-surface-3"
    >
      +
    </button>
  </div>
);

/** Full-screen οθόνη πάνω από το app (παραγγελία, πληρωμή). */
export const FullScreen: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Με ετικέτα: βέλος «πίσω» αντί για ✕ (υπο-σελίδα της οθόνης, π.χ. η σύνοψη της παραγγελίας). */
  backLabel?: string;
  footer?: React.ReactNode;
  /** false = το περιεχόμενο κυλά μόνο του (π.χ. σελίδες με σύρσιμο, η καθεμιά με δική της κύλιση). */
  scroll?: boolean;
  /** Κουμπιά δεξιά στην κεφαλίδα (π.χ. ρυθμίσεις συσκευής). */
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, onClose, backLabel, footer, scroll = true, actions, children }) => (
  <Overlay>
    <div
      className="z-[100] bg-surface-0 flex flex-col"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <header className="role-header bg-surface-1 border-b border-line px-2 py-2 flex items-center gap-1.5 shrink-0 wa-safe-top">
        <IconButton onClick={onClose} label={backLabel ?? 'Κλείσιμο'}>
          {backLabel ? <ChevronLeft className="w-7 h-7" /> : <X className="w-6 h-6" />}
        </IconButton>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-t1 text-lg leading-tight truncate">{title}</h2>
          {subtitle && <p className="text-sm text-t3 leading-tight truncate">{subtitle}</p>}
        </div>
        {actions}
      </header>
      <div className={`flex-1 min-h-0 ${scroll ? 'overflow-y-auto wa-scroll' : 'overflow-hidden'}`}>{children}</div>
      {footer && (
        <div className="border-t border-line bg-surface-1 px-3 pt-2 shrink-0 wa-safe-bottom">
          {footer}
        </div>
      )}
    </div>
  </Overlay>
);

/** Bottom sheet — το φυσικό μοτίβο του κινητού για λεπτομέρειες. */
export const Sheet: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, onClose, footer, children }) => (
  <Overlay>
    <div
      className="z-[100] bg-black/40 dark:bg-black/60 flex items-end"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
    >
      <div
        className="bg-surface-0 w-full rounded-t-3xl flex flex-col wa-sheet wa-animate-up border-t border-line"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-2 flex justify-center shrink-0">
          <span className="w-10 h-1.5 rounded-full bg-line-strong dark:bg-surface-3" />
        </div>
        <header className="role-header px-3 py-2 flex items-start gap-2 border-b border-line shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-t1 text-xl leading-tight truncate">{title}</h2>
            {subtitle && <p className="text-sm text-t3 leading-tight mt-0.5">{subtitle}</p>}
          </div>
          <IconButton onClick={onClose} label="Κλείσιμο">
            <X className="w-6 h-6" />
          </IconButton>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto wa-scroll">{children}</div>
        {footer && (
          <div className="border-t border-line px-3 pt-2 shrink-0 wa-safe-bottom">{footer}</div>
        )}
      </div>
    </div>
  </Overlay>
);

export const EmptyState: React.FC<{
  icon: React.ElementType;
  title: string;
  hint?: string;
}> = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-10 px-4">
    <Icon className="w-12 h-12 mx-auto mb-3 text-t4" />
    <p className="font-bold text-base text-t2">{title}</p>
    {hint && <p className="text-sm text-t4 mt-1">{hint}</p>}
  </div>
);

/** Κλασική «€2.60» όπως πάντα· «Νέα εμφάνιση» «2,60 €» (utils/formatMoney). Άκυρο ποσό → 0. */
export const money = (v: any): string => formatMoney(parseFloat(String(v ?? 0)) || 0);
